import * as React from "react";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { IQuickJournalState } from "source/app/presentation/BottomBar/QuickJournal/Data/QuickJournalTypes";
import { GetLocalDayHourKey } from "source/app/presentation/BottomBar/QuickJournal/Data/QuickJournalKeys";
import { PruneQuickJournalHourlyCounts } from "source/app/presentation/BottomBar/QuickJournal/Data/Stats/QuickJournalStatsCore";
import { GetQuickJournalCurrentTimeStamp, GetQuickJournalNow } from "source/app/presentation/BottomBar/QuickJournal/Data/Clock/QuickJournalClock";
import { Sounds } from "source/app/services/Sound/Sounds";
import { ReadState } from "source/app/services/UI/State/ReadState";
import { SetState } from "source/app/services/UI/State/SetState";
import { IsIosDevice } from "source/app/presentation/BottomBar/QuickJournal/View/QuickJournalLayout";
import type { IUseJournalCaptureTextAreaInteractionsProps } from "./useJournalCaptureTextArea";

export function useJournalCaptureTextAreaInteractions({
    value,
    onChange,
    onFocusChange,
    onFocusedStateChange,
    onEnter,
    textAreaRef,
    autoAdjustHeight,
    recordTypingSample,
}: IUseJournalCaptureTextAreaInteractionsProps) {
    const submissionLockTimeoutRef = React.useRef<number | undefined>(undefined);
    const isSubmittingCurrentInputRef = React.useRef(false);
    const shouldIgnoreNextLineBreakChangeRef = React.useRef(false);
    const disabledFormControlsRef = React.useRef<Array<{ element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement; wasDisabled: boolean }>>([]);
    React.useEffect(() => {
        return () => {
            if (submissionLockTimeoutRef.current) {
                window.clearTimeout(submissionLockTimeoutRef.current);
            }
        };
    }, []);

    const restoreOtherFormControls = React.useCallback(() => {
        disabledFormControlsRef.current.forEach(({ element, wasDisabled }) => {
            if (element.isConnected) {
                element.disabled = wasDisabled;
            }
        });

        disabledFormControlsRef.current = [];
    }, []);

    const disableOtherFormControlsForIosSafari = React.useCallback(() => {
        if (!IsIosDevice() || !textAreaRef.current) {
            return;
        }

        restoreOtherFormControls();

        const activeTextArea = textAreaRef.current;
        const formControls = document.querySelectorAll<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >("input, textarea, select");

        disabledFormControlsRef.current = Array.from(formControls)
            .filter((element) => element !== activeTextArea)
            .map((element) => {
                const wasDisabled = element.disabled;
                element.disabled = true;

                return { element, wasDisabled };
            });
    }, [restoreOtherFormControls, textAreaRef]);

    React.useEffect(() => restoreOtherFormControls, [restoreOtherFormControls]);
    const submitQuickJournalEntry = React.useCallback(
        (
            event:
                | React.KeyboardEvent<HTMLTextAreaElement>
                | React.FormEvent<HTMLTextAreaElement>,
            valueOverride?: string
        ) => {
            const submittedValue = valueOverride ?? value;
            const trimmedValue = submittedValue.trim();

            if (!trimmedValue.length) {
                event.preventDefault();
                return false;
            }

            if (isSubmittingCurrentInputRef.current) {
                event.preventDefault();
                return false;
            }

            isSubmittingCurrentInputRef.current = true;

            if (submissionLockTimeoutRef.current) {
                window.clearTimeout(submissionLockTimeoutRef.current);
            }

            submissionLockTimeoutRef.current = window.setTimeout(() => {
                isSubmittingCurrentInputRef.current = false;
                submissionLockTimeoutRef.current = undefined;
            }, 0);

            const quickJournalState = ReadState<IQuickJournalState>(
                ESharedFunction.TypeAwayInputBox_state
            )!;
            const createdAt = GetQuickJournalNow().toISOString();
            const createdAtBucketKey = GetLocalDayHourKey(createdAt);

            SetState<IQuickJournalState>(
                ESharedFunction.TypeAwayInputBox_state,
                {
                    recentEntries: [
                        ...quickJournalState.recentEntries,
                        {
                            text: submittedValue,
                            timeStamp: GetQuickJournalCurrentTimeStamp(),
                            createdAt,
                        },
                    ],
                    weeklyHourlyCounts: PruneQuickJournalHourlyCounts({
                        ...quickJournalState.weeklyHourlyCounts,
                        [createdAtBucketKey]:
                            (quickJournalState.weeklyHourlyCounts[
                                createdAtBucketKey
                            ] ?? 0) + 1,
                    }),
                    draft: "",
                    animatingEntryCreatedAt: [createdAt],
                }
            );

            Sounds.paper_sliding.Play();
            recordTypingSample(1);
            onEnter?.(trimmedValue);
            event.preventDefault();
            return true;
        },
        [onEnter, recordTypingSample, value]
    );
    return {
        handleClick: () => {
            Sounds.ambientHalo.Play();
            autoAdjustHeight();
        },
        handleChange: (nextValue: string) => {
            if (
                shouldIgnoreNextLineBreakChangeRef.current &&
                nextValue.includes("\n")
            ) {
                shouldIgnoreNextLineBreakChangeRef.current = false;
                return;
            }

            shouldIgnoreNextLineBreakChangeRef.current = false;
            onChange(nextValue);
        },
        handleBlur: () => {
            restoreOtherFormControls();
            onFocusedStateChange(false);
            onFocusChange?.(false);
        },
        handleFocus: () => {
            disableOtherFormControlsForIosSafari();
            onFocusedStateChange(true);
            onFocusChange?.(true);
        },
        handleBeforeInput: (
            event: React.FormEvent<HTMLTextAreaElement>
        ) => {
            const nativeEvent = event.nativeEvent as InputEvent;

            if (nativeEvent.inputType === "insertLineBreak") {
                submitQuickJournalEntry(event);
            }
        },
        handleInput: (event: React.FormEvent<HTMLTextAreaElement>) => {
            const nativeEvent = event.nativeEvent as InputEvent;
            const inputValue = event.currentTarget.value;

            if (
                nativeEvent.inputType === "insertLineBreak" ||
                inputValue.includes("\n")
            ) {
                shouldIgnoreNextLineBreakChangeRef.current = true;
                submitQuickJournalEntry(
                    event,
                    inputValue.replace(/\n/g, "")
                );
            }
        },
        handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === "Enter" && !event.shiftKey) {
                submitQuickJournalEntry(event);
            }
        },
    };
}
